<template>
  <div class="app">
    <div v-if="isStandAlone" class="app__stand-alone">
      <header class="header">Mijn pensioenfonds</header>
      <div class="app__select-fonds">
        <h1>Selecteer uw pensioenfonds</h1>
        <select-window
                :items="filteredFondsen"
                :placeholderText="'Zoek een pensioenfonds'"
                :search-text="searchText"
                v-on:select="selectHandler"
                v-on:filterchange="filterChangeHandler">
        </select-window>
      </div>
      <h1>Pensioenkorting</h1>
      <actuele-dekkingsgraad :fonds="currentFonds"></actuele-dekkingsgraad>
      <h1>Hoe was de ontwikkeling van de dekkingsgraad over de afgelopen jaren?</h1>
      <dekkingsgraad :fonds="currentFonds"></dekkingsgraad>

      <h1>Hoe rijk is mijn pensioenfonds?</h1>
      <belegd-vermogen :fonds="currentFonds"></belegd-vermogen>

      <h1>Hoe doet mijn pensioenfonds in vergelijking met andere fondsen?</h1>
      <compare :fondsen="comparableFondsen" :fonds="currentFonds"></compare>

      <h1>Hoe is de verhouding tussen premiebetalers en mensen die pensioen ontvangen?</h1>
      <deelnemers :fonds="currentFonds"></deelnemers>

      <h1>Hoeveel geeft uw pensioenfonds uit aan de kosten van het beheer?</h1>
      <kosten-beheer :fondsen="comparableFondsen" :fonds="currentFonds"></kosten-beheer>
      <h1>Betaal ik genoeg premie voor mijn pensioen?</h1>
      <premiedekkingsgraad :fonds="currentFonds"></premiedekkingsgraad>

      <h1 class="h1--bekijk-ook">Bekijk ook</h1>
      <bekijk-ook></bekijk-ook>
      <div class="logo">
        <div class="logo__image"></div>
      </div>
    </div>
    <div v-if="!isStandAlone" class="app__splash" @click="goToStandAlone">
    </div>
  </div>
</template>

<script>
import Papa from "papaparse";
import { find, trim, sortBy } from "lodash";
import SelectWindow from "./components/SelectWindow";
import ActueleDekkingsgraad from "./components/ActueleDekkingsgraad";
import Dekkingsgraad from "./components/Dekkingsgraad";
import Compare from "./components/Compare";
import KostenBeheer from "./components/KostenBeheer";
import Premiedekkingsgraad from "./components/Premiedekkingsgraad";
import Deelnemers from "./components/Deelnemers";
import BelegdVermogen from "./components/BelegdVermogen";
import BekijkOok from "./components/BekijkOok";

const propertyMapping = [
  {
    pattern: 'Aantal deelnemers',
    targetKey: 'deelnemers',
    isNumeric: true
  },
  {
    pattern: 'Aantal gewezen deelnemers',
    targetKey: 'gewezenDeelnemers',
    isNumeric: true
  },
  {
    pattern: 'Aantal pensioengerechtigden',
    targetKey: 'pensioengerechtigden',
    isNumeric: true
  },
  {
    pattern: 'Totale aantal deelnemers',
    targetKey: 'deelnemersTotaal',
    isNumeric: true
  },
  {
    pattern: 'Administratieve uitvoeringskosten per deelnemer',
    targetKey: 'uitvoeringskosten',
    isNumeric: true
  },
  {
    pattern: 'Beleggingen voor risico fonds',
    targetKey: 'beleggingen',
    isNumeric: true
  },
  {
    pattern: 'Premiedekkingsgraad',
    targetKey: 'premiedekkingsgraad',
    isNumeric: true
  },
  {
    pattern: 'Re�le dekkingsgraad',
    targetKey: 'rdekkingsgraad',
    isNumeric: true
  },
  {
    pattern: 'Post',
    targetKey: 'fonds',
    isNumeric: false
  },
  {
    pattern: 'Vereiste dekkingsgraad',
    targetKey: 'vereisteDekkingsgraad',
    isNumeric: true
  },
  {
    pattern: 'ACT DG',
    targetKey: 'actueledekkingsgraad',
    isNumeric: true
  }
];

export default {
  name: "app",
  components: {ActueleDekkingsgraad, BekijkOok, BelegdVermogen, Deelnemers, KostenBeheer, Premiedekkingsgraad,
    Compare, Dekkingsgraad, SelectWindow},
  data: () => {
    return {
      fondsen: [],
      filteredFondsen: [],
      comparableFondsen: [],
      searchText: null,
      currentFonds: null,
      error: false,
      loaded: false,
      isStandAlone: false
    };
  },
  mounted() {
    this.isStandAlone = window.location == window.parent.location;

    const baseUrl = process.env.VUE_APP_BASE_URL;
    const self = this;
    Papa.parse(`${baseUrl}/pensioenfondsen.csv`, {
      dynamicTyping: false,
      complete: results => {
        this.parseData(results);
        const currentFonds = this.fondsen.length > 0 ? this.fondsen[0] : null;
        this.selectHandler(currentFonds);
        this.loaded = true;
      },
      error(err) {
        console.log(err);
        this.error = true;
      },
      download: true,
      delimiter: ";",
      header: true
    });
  },
  methods: {
    parseData(response) {

      this.fondsen = response.data.map( (fonds, index) => {
        /* Map properties */
        const result = { id: index };
        const originalProperties = Object.keys(fonds);
        /* Find properties in the source data that match the patterns, e.g. 'Aantal deelnemers' matches 'Aantal deelnemers2017' */
        propertyMapping.forEach(mapping => {
          const match = find(originalProperties, property => property.search(mapping.pattern) === 0);
          if (match) {
            if (mapping.isNumeric) {
              const tmp = fonds[match]
                      .replace(/\./g, '')
                      .replace(',', '.')
                      .replace('%', '');
              result[mapping.targetKey] = Number(tmp);
            } else {
              result[mapping.targetKey] = fonds[match];
            }
          }
        });
        /* Get the time series */
        const series = [];
        const regExp = /DG\s(\d+)([km])(\d+)/i;
        originalProperties.forEach(property => {
          const match = regExp.exec(property);
          if (match) {
            const year = match[1];
            const indexType = match[2].toLowerCase() === 'm' ? 'month' : 'quarter';
            const index = Number(match[3]);
            let month = indexType === 'quarter' ? 3 * index - 1 : index - 1;
           /* if (month < 10) {
              month = `0${month}`;
            }*/
            const date = new Date(year, month, 2);
            //const date = new Date(`${year}-${month}-02`); /* If we use the first of the month, Highcharts displays the wrong month for k1.*/
            const timestamp = date.getTime();
            let value = fonds[property];
            if (value) {
              value = typeof value == 'string' ? Number(value.replace(',', '.')) : value;
              const dataPoint = { date, timestamp, value };
              series.push(dataPoint);
            }
          }
        });
        result.series = sortBy(series, ['date']);
        return result;
      });
      this.fondsen = this.fondsen.filter(fonds => fonds.fonds);
    },
    selectHandler(fonds) {
      this.currentFonds = fonds;
      this.searchText = fonds ? fonds.fonds : null;
      this.filteredFondsen = [];
      this.comparableFondsen = this.getComparableFondsen();
    },
    filterChangeHandler(text) {
      text = trim(text);
      if (text.length > 0) {
        text = text.toLowerCase();
        this.filteredFondsen = this.fondsen.filter(fonds => fonds.fonds.toLowerCase().search(text) !== -1);
      } else {
        this.filteredFondsen = this.fondsen;
      }
    },
    getComparableFondsen() {
      const fondsen = sortBy(this.fondsen, fonds => {
        return Math.abs(fonds.beleggingen - this.currentFonds.beleggingen)
      });
      return fondsen.slice(0, 6);
    },
    goToStandAlone() {
      const url = window.location.href;
      window.open(url);
    }
  }
};
</script>

<style lang="stylus">
@import 'styles/reset.css';

*, *:before, *:after
  box-sizing border-box

html
  width 100%
  height 100%

body
  font-family "Helvetica Neue", Helvetica, Arial, sans-serif
  line-height 1.25em
  font-weight normal
  color #333
  height 100%

h1
  font-weight bold
  margin 2em 0 .5em 0
  .app__select-fonds &
    margin-top 0
  &.h1--bekijk-ook
    color #666

p
 margin .5em 0

.app
  height 100%

.app__stand-alone
  margin 0 $gutter
  height 100%

.app__select-fonds
  padding $gutter;
  background-color $color-grey-background
  margin 0 (-($gutter))

.header
  background-color $color-primary
  font-family 'Merriweather', serif
  padding $gutter
  margin 0 (-($gutter))
  color #fff
  font-size 1.2em

.logo
  padding $gutter 0
  border-top 1px solid $color-grey-border

.logo__image
  background-repeat: no-repeat;
  background-position: top left;
  background-size contain
  background-image: url('data:image/svg+xml;base64,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');
  height: 20px

@media only screen and (min-width: 830px)
  .app
    width 800px
    margin 0 auto

.app__splash
  background-color $color-primary
  width 100%
  height 100%
  background-image url('./assets/iframe-pensioentool.jpg')
  background-position center
  background-repeat no-repeat
  background-size cover

.app__splash-title
  font-family 'Merriweather', serif
  font-size 1.8em
  line-height 1.2em
  color #fff
  padding $gutter
  text-shadow 0 0 4px rgba(100, 100, 100, .9)

.comment
  font-size .9em
  color $color-secondary-text
</style>
